/**
 * Typed fetch client. Single chokepoint for all HTTP — every service
 * file talks to the backend through this.
 *
 * Built-in defenses
 *  - Bounded timeouts (AbortController) → no hung sockets.
 *  - AbortSignal forwarding → callers can cancel on unmount / param change.
 *  - Auth header read from in-memory store (NOT localStorage). Pair this
 *    with httpOnly refresh cookies on the backend so XSS can't steal a
 *    long-lived token.
 *  - Idempotent retries on transient 5xx / network blips with jitter.
 *  - Domain error mapping → call sites use `instanceof`, not status codes.
 *  - Optional response-shape validation via Zod schemas.
 *
 * Usage
 *
 *   const user = await api.get<User>('/v1/users/me', { schema: UserSchema });
 *   await api.post('/v1/items', { name }, { signal });
 */

import { z, type ZodSchema } from 'zod';
import { logger } from '@/lib/logger';
import { platform } from '@/lib/platform';
import {
  ApiError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  RateLimitError,
  ServerError,
  UnauthorizedError,
  ValidationError,
} from './errors';

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

// Fail-loud guard: a release bundle with no API URL — or one pointing at
// localhost — almost certainly means `.env.production` wasn't updated
// before `vite build`. Crash at module load rather than silently firing
// requests into the void from a TestFlight / App Store build.
if (
  import.meta.env.PROD &&
  (!BASE_URL || /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)/.test(BASE_URL))
) {
  throw new Error(
    'Production build is missing VITE_API_BASE_URL or points at localhost. ' +
      'Set the prod API URL in nynjwc-frontend/.env.production before running `npm run build`.',
  );
}

const DEFAULT_TIMEOUT_MS = 15_000;
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 2;

// ── Auth token store ─────────────────────────────────────────────────────
// Kept in-memory so XSS can't read it from storage. Pair with httpOnly
// refresh cookie on the backend; refresh on app boot to repopulate.
let authToken: string | null = null;
export function setAuthToken(token: string | null): void {
  authToken = token;
}
export function getAuthToken(): string | null {
  return authToken;
}

// Invoked when a request authed with an *account* token (`acct_…`) gets a 401 —
// the account was deleted/rotated server-side. The session layer registers a
// handler that drops the account token and falls back to the device install id
// so the app never bricks on a stale account credential (spike P1 #6).
const ACCOUNT_TOKEN_PREFIX = 'acct_';
let onAccountAuthError: (() => void) | null = null;
export function setAccountAuthErrorHandler(fn: (() => void) | null): void {
  onAccountAuthError = fn;
}

// ── Request types ────────────────────────────────────────────────────────
type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions<T> {
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
  /** Optional Zod schema. If provided, the response body is validated before being returned. */
  schema?: ZodSchema<T>;
  /** Override retry policy for this call. */
  retries?: number;
  /**
   * If set, sent as the `Idempotency-Key` header. Use on POSTs that the
   * backend expects to be idempotent. Generate with `crypto.randomUUID()`.
   */
  idempotencyKey?: string;
}

// ── Internal helpers ─────────────────────────────────────────────────────
function buildUrl(path: string, query?: RequestOptions<unknown>['query']): string {
  // Encode each path segment so caller-supplied ids can't break out.
  const url = new URL(path, BASE_URL || window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

function combineSignals(signals: (AbortSignal | undefined)[]): AbortSignal {
  // AbortSignal.any landed in modern browsers; fall back to a manual combiner.
  const filtered = signals.filter((s): s is AbortSignal => !!s);
  if (filtered.length === 1) return filtered[0]!;
  if ('any' in AbortSignal && typeof (AbortSignal as unknown as { any: (s: AbortSignal[]) => AbortSignal }).any === 'function') {
    return (AbortSignal as unknown as { any: (s: AbortSignal[]) => AbortSignal }).any(filtered);
  }
  const ctrl = new AbortController();
  for (const s of filtered) {
    if (s.aborted) ctrl.abort(s.reason);
    else s.addEventListener('abort', () => ctrl.abort(s.reason), { once: true });
  }
  return ctrl.signal;
}

interface ErrorBody {
  message?: string;
  code?: string;
  fieldErrors?: Record<string, string>;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ErrorBody | null = null;
  try {
    body = (await res.clone().json()) as ErrorBody;
  } catch {
    // Non-JSON error body — fall through to status-based message.
  }
  const message = body?.message ?? res.statusText ?? 'Request failed';

  switch (res.status) {
    case 401:
      return new UnauthorizedError(message);
    case 403:
      return new ForbiddenError(message);
    case 404:
      return new NotFoundError(message);
    case 422:
      return new ValidationError(message, body?.fieldErrors);
    case 429: {
      const retryAfter = Number(res.headers.get('Retry-After'));
      return new RateLimitError(message, Number.isFinite(retryAfter) ? retryAfter : undefined);
    }
    default:
      if (res.status >= 500) return new ServerError(message, res.status);
      return new ApiError(message, res.status, body?.code);
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ── Core ─────────────────────────────────────────────────────────────────
async function request<T>(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions<T> = {},
): Promise<T> {
  const url = buildUrl(path, options.query);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const maxRetries = options.retries ?? (RETRYABLE_METHODS.has(method) ? MAX_RETRIES : 0);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    ...(options.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : {}),
    ...options.headers,
  };

  let attempt = 0;
  while (true) {
    const timeoutCtrl = new AbortController();
    const timer = setTimeout(() => timeoutCtrl.abort(new Error('Request timed out')), timeoutMs);
    const signal = combineSignals([options.signal, timeoutCtrl.signal]);

    try {
      const init: RequestInit = {
        method,
        headers,
        signal,
        // Web: send the httpOnly refresh cookie. Native: WebView origin
        // doesn't match the API origin, so cookies are unreliable —
        // AuthProvider passes the refresh token via header instead.
        credentials: platform.isNative ? 'omit' : 'include',
        // Defense in depth: stop the browser from using cached responses for mutations.
        cache: method === 'GET' ? 'default' : 'no-store',
      };
      if (body !== undefined) init.body = JSON.stringify(body);
      const res = await fetch(url, init);

      if (!res.ok) {
        const err = await parseError(res);
        if (res.status === 401 && authToken?.startsWith(ACCOUNT_TOKEN_PREFIX)) {
          // Stale account token — let the session layer revert to the install id.
          onAccountAuthError?.();
        }
        if (
          attempt < maxRetries &&
          RETRYABLE_METHODS.has(method) &&
          RETRYABLE_STATUSES.has(res.status)
        ) {
          attempt += 1;
          await sleep(2 ** attempt * 200 + Math.random() * 200);
          continue;
        }
        throw err;
      }

      if (res.status === 204) return undefined as T;

      const data = (await res.json()) as unknown;
      if (options.schema) {
        const parsed = options.schema.safeParse(data);
        if (!parsed.success) {
          logger.error('Response failed schema validation', parsed.error, { url, method });
          throw new ApiError('Malformed response from server', res.status, 'SCHEMA_MISMATCH');
        }
        return parsed.data;
      }
      return data as T;
    } catch (err) {
      clearTimeout(timer);
      if (err instanceof ApiError) throw err;
      if ((err as Error)?.name === 'AbortError') throw err;
      // Network failure → retry GETs / HEADs.
      if (attempt < maxRetries && RETRYABLE_METHODS.has(method)) {
        attempt += 1;
        await sleep(2 ** attempt * 200 + Math.random() * 200);
        continue;
      }
      throw new NetworkError((err as Error)?.message ?? 'Network error');
    } finally {
      clearTimeout(timer);
    }
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions<T>) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions<T>) => request<T>('DELETE', path, undefined, options),
};

// Re-export zod so service files don't need a separate import for schemas.
export { z };
